import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

export type Preset = "7d" | "30d" | "90d" | "all";
const DAYS: Record<Exclude<Preset, "all">, number> = { "7d": 7, "30d": 30, "90d": 90 };

interface TimeframeValue {
	preset: Preset;
	setPreset: (p: Preset) => void;
	from?: string;
	to?: string;
	qs: string;
}

const Ctx = createContext<TimeframeValue | null>(null);

export function TimeframeProvider({ children }: { children: ReactNode }) {
	const [preset, setPreset] = useState<Preset>(
		(localStorage.getItem("en_timeframe") as Preset) ?? "30d",
	);
	const value = useMemo<TimeframeValue>(() => {
		let from: string | undefined;
		let to: string | undefined;
		if (preset !== "all") {
			to = new Date().toISOString();
			from = new Date(Date.now() - DAYS[preset] * 86400_000).toISOString();
		}
		const params = new URLSearchParams();
		if (from) params.set("from", from);
		if (to) params.set("to", to);
		return { preset, setPreset: (p) => {
			localStorage.setItem("en_timeframe", p);
			setPreset(p);
		}, from, to, qs: params.toString() };
	}, [preset]);
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTimeframe(): TimeframeValue {
	const v = useContext(Ctx);
	if (!v) throw new Error("useTimeframe outside provider");
	return v;
}

const PRESETS: Preset[] = ["7d", "30d", "90d", "all"];

export function TimeframeSelect() {
	const { preset, setPreset } = useTimeframe();
	return (
		<div className="flex rounded-md border border-line overflow-hidden">
			{PRESETS.map((p) => (
				<button
					key={p}
					type="button"
					onClick={() => setPreset(p)}
					className={`px-3 py-1.5 text-sm ${
						preset === p ? "bg-primary text-white" : "bg-surface text-muted hover:bg-surface-2"
					}`}
				>
					{p}
				</button>
			))}
		</div>
	);
}
